// src/pages/KundeFormPage.jsx
// Kunde anlegen oder bearbeiten (Auftraggeber-Stammdaten).

import { useState, useEffect, useRef } from 'react';
import { db, supabase, withTimeout, geaenderteFelder } from '../lib/supabase';
import { useKunde } from '../hooks/useKunden';
import { C, Field, TextInput, Row, Subsection, zurueckStil } from './formui';

const EMPTY = {
  kunden_nr: '', name: '',
  strasse: '', plz: '', ort: '',
  ansprechpartner: '', position: '',
  telefon: '', mobil: '', email: '',
  notizen: '',
};

export default function KundeFormPage({ kundeId, onCancel, onSaved }) {
  const isEditMode = !!kundeId;
  const { kunde, loading, error: ladeFehler } = useKunde(kundeId);

  const [data, setData] = useState({ ...EMPTY });
  const [populated, setPopulated] = useState(!isEditMode);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  // Stand beim Oeffnen: gespeichert werden nur Abweichungen (sonst ueberschrieb
  // das Formular eine inzwischen im Protokoll geaenderte Telefonnummer).
  const vorher = useRef(null);

  useEffect(() => {
    if (!kundeId || populated || !kunde) return;
    const geladen = ({
      kunden_nr: kunde.kunden_nr || '', name: kunde.name || '',
      strasse: kunde.strasse || '', plz: kunde.plz || '', ort: kunde.ort || '',
      ansprechpartner: kunde.ansprechpartner || '', position: kunde.position || '',
      telefon: kunde.telefon || '', mobil: kunde.mobil || '', email: kunde.email || '',
      notizen: kunde.notizen || '',
    });
    setData(geladen);
    vorher.current = geladen;
    setPopulated(true);
  }, [kunde, kundeId, populated]);

  const set = (k, v) => setData(d => ({ ...d, [k]: v }));

  const save = async () => {
    if (!data.name.trim()) { setMsg({ type: 'error', text: 'Bitte Name / Firma eingeben.' }); return; }
    if (saving) return;
    setSaving(true); setMsg(null);
    // Nach Erfolg bleibt der Knopf gesperrt bis zum Seitenwechsel - vorher
    // war er 0,4 s wieder frei, ein zweiter Tipp legte den Kunden doppelt an.
    let fertig = false;
    try {
      const zeile = (d) => ({ ...d, name: (d.name || '').trim(), kunden_nr: d.kunden_nr || null });
      let saved;
      if (isEditMode && vorher.current) {
        const patch = geaenderteFelder(zeile(data), zeile(vorher.current));
        if (Object.keys(patch).length > 0) {
          const { data: rows, error } = await withTimeout(
            supabase.from('kunden').update(patch).eq('id', kundeId).select('id'), 15000, 'Kunde speichern');
          if (error) throw error;
          if (!rows || rows.length === 0) throw new Error('Kunde wurde nicht gespeichert (gelöscht oder keine Berechtigung).');
        }
        saved = { id: kundeId };
        vorher.current = { ...data };
      } else {
        const payload = { ...zeile(data), active: true };
        if (isEditMode) payload.id = kundeId;
        const { data: s, error } = await db.kunden.save(payload);
        if (error) throw error;
        saved = s;
      }
      setMsg({ type: 'success', text: 'Kunde gespeichert.' });
      fertig = true;
      if (onSaved) setTimeout(() => onSaved(saved.id), 400);
      else setSaving(false);
    } catch (e) {
      setMsg({ type: 'error', text: 'Speichern fehlgeschlagen: ' + (e.message || 'Unbekannt') });
    } finally {
      if (!fertig) setSaving(false);
    }
  };

  if (isEditMode && loading && !populated) {
    return <div style={{ padding: 32, textAlign: 'center', color: C.gray500 }}>Lade Kunde...</div>;
  }
  // Laden gescheitert: KEIN leeres Formular zeigen. Speichern haette sonst
  // Anschrift und Ansprechpartner des Kunden mit Leerem ueberschrieben.
  if (isEditMode && !populated) {
    return (
      <div style={{ padding: 32, textAlign: 'center' }}>
        <div style={{ color: C.red, marginBottom: 14, fontSize: 15 }}>
          Der Kunde konnte nicht geladen werden{ladeFehler ? ': ' + ladeFehler : '.'} Bitte bei besserem Netz nochmal öffnen.
        </div>
        <button onClick={onCancel} style={{ padding: '10px 18px', background: C.gray100, color: C.navy, border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 15 }}>Zurück</button>
      </div>
    );
  }

  return (
    <div style={{ background: C.bg, minHeight: '100vh', fontFamily: "system-ui,-apple-system,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif" }}>
      <div style={{ background: C.navy, padding: '14px', boxShadow: '0 2px 14px rgba(0,0,0,0.22)' }}>
        <div style={{ maxWidth: 1400, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={onCancel} style={zurueckStil}>‹ Zurück</button>
          <div>
            <div style={{ color: C.white, fontWeight: 800, fontSize: 16 }}>{isEditMode ? 'Kunde bearbeiten' : '+ Neuer Kunde'}</div>
            <div style={{ color: C.gray300, fontSize: 14 }}>Auftraggeber-Stammdaten</div>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1400, margin: '0 auto', padding: '20px 14px 100px' }}>
        <div style={{ background: C.white, borderRadius: 10, padding: 22, boxShadow: '0 1px 6px rgba(10,22,40,0.06)' }}>

          <Subsection title='Kunden-Identifikation'>
            <Row columns={2}>
              <Field label='Kundennummer'><TextInput value={data.kunden_nr} onChange={v => set('kunden_nr', v)}/></Field>
              <Field label='Name / Firma' required hint='Name der Privatperson oder Firmenname'><TextInput value={data.name} onChange={v => set('name', v)}/></Field>
            </Row>
          </Subsection>

          <Subsection title='Anschrift'>
            <Field label='Strasse'><TextInput value={data.strasse} onChange={v => set('strasse', v)}/></Field>
            <Row columns={2}>
              <Field label='PLZ'><TextInput value={data.plz} onChange={v => set('plz', v)}/></Field>
              <Field label='Ort'><TextInput value={data.ort} onChange={v => set('ort', v)}/></Field>
            </Row>
          </Subsection>

          <Subsection title='Ansprechpartner'>
            <Row columns={2}>
              <Field label='Name'><TextInput value={data.ansprechpartner} onChange={v => set('ansprechpartner', v)}/></Field>
              <Field label='Position' hint='z.B. Geschaeftsfuehrer, Hausverwaltung'><TextInput value={data.position} onChange={v => set('position', v)}/></Field>
            </Row>
            <Row columns={3}>
              <Field label='Telefon'><TextInput value={data.telefon} onChange={v => set('telefon', v)}/></Field>
              <Field label='Mobil' hint='Wichtig für Erreichbarkeit'><TextInput value={data.mobil} onChange={v => set('mobil', v)}/></Field>
              <Field label='E-Mail'><TextInput value={data.email} onChange={v => set('email', v)} type='email'/></Field>
            </Row>
          </Subsection>

          <Field label='Notizen'><TextInput value={data.notizen} onChange={v => set('notizen', v)}/></Field>

          {msg && (
            <div style={{ padding: 12, marginTop: 14, borderRadius: 8,
              background: msg.type === 'error' ? C.redBg : C.greenBg,
              color: msg.type === 'error' ? C.red : C.green, fontSize: 15, fontWeight: 600 }}>
              {msg.type === 'error' ? '! ' : 'OK '}{msg.text}
            </div>
          )}
        </div>
      </div>

      <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: C.white, borderTop: '1px solid ' + C.gray200, boxShadow: '0 -2px 14px rgba(0,0,0,0.08)', padding: '12px 14px' }}>
        <div style={{ maxWidth: 1400, margin: '0 auto', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onCancel} disabled={saving} style={{ padding: '10px 16px', background: C.gray100, color: C.navy, border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 15 }}>Abbrechen</button>
          <button onClick={save} disabled={saving} style={{ padding: '10px 22px', background: saving ? C.gray300 : C.yellow, color: C.navy, border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 800, fontSize: 14 }}>{saving ? 'Speichert...' : 'Speichern'}</button>
        </div>
      </div>
    </div>
  );
}
